import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  ElasticResponseItem,
  ICCassetteGroup,
  ICCassetteInfo,
  ICExperiment,
  ICInstrumentOrder,
  ICInstrumentOrderReplacement,
  ICLegendChangeType,
  ICLegendData,
  ICLegendDataIteration,
} from 'src/app/services/labpartner.service.model';
import { UserAccountService } from 'src/app/services/user-account.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import { ICRoundService } from '../ic-round.service';
import { ICImagesDialogComponent } from '../images-dialog/ic-images-dialog.component';
import { ICExcludeIncludeDialogComponent } from './exclude-include-dialog/ic-exclude-include-dialog.component';

export interface ICAdjustLegendComponentData {
  legendData: ICLegendData[];
  testResults: ElasticResponseItem[];
  legendDataIteration: ICLegendDataIteration[];
  cassetteInfo?: ICCassetteInfo[];
  icExperimentId: number;
  experiment: ICExperiment;
}

@Component({
  selector: 'ic-adjust-legend',
  templateUrl: './ic-adjust-legend.component.html',
  styleUrls: ['./ic-adjust-legend.component.scss'],
  standalone: false,
})
export class ICAdjustLegendComponent extends BaseComponent implements OnInit {
  experimentId: number = 0;

  experiment: ICExperiment = {} as ICExperiment;

  legendData?: ICLegendData[];

  dataSource: MatTableDataSource<ICLegendData> = new MatTableDataSource<ICLegendData>();

  displayedColumns: string[] = ['legendDataId', 'name', 'cassettes'];

  testResults: ElasticResponseItem[] = [];

  legendDataIterations: ICLegendDataIteration[] = [];

  cassetteInfo: ICCassetteInfo[] = [];

  newCassetteInfo: ICCassetteInfo[] = [];

  icExperimentId: number = 0;

  rounds: number[] = [];

  selectedRound: number = 0;

  @ViewChildren('dropListRef') dropLists!: QueryList<any>;
  connectedDropLists: any[] = [];

  currentRoles: string[] = [];

  cassettes: ICCassetteGroup[] = [];

  cassetteLiveNotesCountMap?: { [key: number]: number } = {};

  private readonly _destroying$ = new Subject<void>();

  private isPrimaryCassetteUpload(cassetteNumber?: string | null): boolean {
    const normalizedCassetteNumber = cassetteNumber?.trim();
    if (!normalizedCassetteNumber) {
      return false;
    }

    const parts = normalizedCassetteNumber.split('.');
    return parts.length === 1 || parts[1] === '1';
  }

  private isExtraCassetteUpload(cassetteNumber?: string | null): boolean {
    const normalizedCassetteNumber = cassetteNumber?.trim();
    if (!normalizedCassetteNumber) {
      return false;
    }

    return !this.isPrimaryCassetteUpload(normalizedCassetteNumber);
  }

  constructor(
    public dialogRef: MatDialogRef<ICAdjustLegendComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICAdjustLegendComponentData,
    private dialog: MatDialog,
    public apiService: LabpartnerService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    public accountService: UserAccountService,
    public icRoundService: ICRoundService,
  ) {
    super();
    this.legendData = data.legendData;
    this.legendDataIterations = data.legendDataIteration;
    this.testResults = data.testResults;
    this.cassetteInfo = data.cassetteInfo || [];
    this.icExperimentId = data.icExperimentId;
    this.experimentId = data.experiment.experimentId;
    this.experiment = data.experiment;
    console.log('ICAdjustLegendComponent initialized with data:', this.data);

    // Ensure each legendDataItem has a cassettes array
    this.legendData.forEach(legendDataItem => {
      legendDataItem.cassettes = [];
    });

    // Initialize rounds from test results
    const roundNumbers = new Set<number>();
    this.testResults.forEach(tr => {
      if (tr.imageCapture && typeof tr.imageCapture.roundNumber === 'number') {
        roundNumbers.add(tr.imageCapture.roundNumber);
      }
    });

    // Remove 0 (All rounds) option and sort
    this.rounds = Array.from(roundNumbers)
      .filter(r => r !== 0)
      .sort((a, b) => a - b);

    // Ensure selectedRound is never 0, default to last round if available
    if (this.rounds.length > 0) {
      if (this.selectedRound === 0 || !this.rounds.includes(this.selectedRound)) {
        this.selectedRound = this.rounds[this.rounds.length - 1]; // Default to the last round
      }
    }

    const serialSet = new Set<string>();
    const serialToInstrumentId = new Map<string, number>();
    const serialToLabel = new Map<string, string>();

    // Extract serials from testResults (primary source - most reliable)
    this.testResults.forEach(tr => {
      const serial = this.getResultInstrumentSerial(tr);
      if (serial) {
      serialSet.add(serial);
      }
    });

    const roundInstrumentOrders = this.getInstrumentOrdersForSelectedRound();

    // Also include instruments from this round's instrument orders to get labels and mark replaced ones
    if (roundInstrumentOrders.length > 0) {
      console.log('[Adjust Legend] instrumentOrders:', roundInstrumentOrders.map(o => ({
        instrumentSerial: o.instrumentSerial,
        originalInstrumentSerial: o.originalInstrumentSerial,
        replacements: o.replacements?.map(r => r.instrumentSerial)
      })));

      roundInstrumentOrders.forEach(order => {
        const activeInstrument = this.getActiveInstrumentForRound(order, this.selectedRound);
        const currentSerial = activeInstrument.instrumentSerial.trim();

        const serialToId = new Map<string, number>();
        const originalSerial = (order.originalInstrumentSerial || '').trim();
        if (originalSerial) {
          serialToId.set(originalSerial, order.originalInstrumentId || order.instrumentId || 0);
        }

        const orderSerial = (order.instrumentSerial || '').trim();
        if (orderSerial) {
          serialToId.set(orderSerial, order.instrumentId || 0);
        }

        (order.replacements || []).forEach(r => {
          const replacementSerial = (r.instrumentSerial || '').trim();
          if (replacementSerial) {
            serialToId.set(replacementSerial, r.instrumentId || 0);
          }
        });

        serialSet.forEach(serial => {
          if (!serialToId.has(serial)) {
            return;
          }

          if (serial === currentSerial) {
            serialToLabel.set(serial, serial);
            serialToInstrumentId.set(serial, activeInstrument.instrumentId || serialToId.get(serial) || 0);
            return;
          }

          serialToLabel.set(serial, `${serial} (replaced)`);
          serialToInstrumentId.set(serial, serialToId.get(serial) || 0);
        });
      });
    }

    // Build availableInstruments array from the serials found in testResults
    this.availableInstruments = Array.from(serialSet).map(serial => ({
      instrumentId: serialToInstrumentId.get(serial) || 0,
      serial: serial,
      label: serialToLabel.get(serial) || serial, // Use label from instrumentOrders if available, otherwise just serial
    }));

    // Sort by serial for consistent ordering
    this.availableInstruments.sort((a, b) => a.serial.localeCompare(b.serial));

    // Auto-select active instrument for selected round if available
    if (this.availableInstruments.length > 0 && !this.selectedInstrumentSerial) {
      const preferredSerial = roundInstrumentOrders.length > 0
        ? this.getActiveInstrumentForRound(roundInstrumentOrders[0], this.selectedRound).instrumentSerial
        : null;

      const preferred = preferredSerial
        ? this.availableInstruments.find(i => i.serial === preferredSerial)
        : undefined;

      this.selectedInstrumentSerial = preferred?.serial || this.availableInstruments[0].serial;
    }

    // Group testResults under legendDataItem > cassette > testResults
    this.testResults.forEach(testResult => {
      testResult.isExtra = this.isExtraCassetteUpload(testResult.imageCapture?.casseteNumber);

      const iteration = this.legendDataIterations.find(
        iteration =>
          iteration.casseteNumber === testResult.imageCapture.casseteNumber.split('.')[0] &&
          testResult.imageCapture.roundNumber === iteration.roundNumber,
      );
      if (!iteration) {
        return;
      }

      const legendDataItem = this.legendData?.find(
        item => item.legendDataId === iteration.legendDataId,
      );
      if (!legendDataItem) {
        return;
      }

      const getSerial = (tr: ElasticResponseItem): string | undefined => {
      return this.getResultInstrumentSerial(tr) ?? undefined;
      };

      const serial = getSerial(testResult);

      let cassette = this.cassettes.find(
        c => c.casseteNumber === testResult.imageCapture.casseteNumber &&
          c.instrumentSerial === serial
      );
      // If cassette does not exist, create it
      if (!cassette) {
        cassette = {
          casseteNumber: testResult.imageCapture.casseteNumber,
          testResults: [testResult],
          legendDataId: legendDataItem.legendDataId,
          cassetteInfo: {
            legendDataId: legendDataItem.legendDataId,
          } as ICCassetteInfo,
          isMoved: false,
          isExtra: this.isExtraCassetteUpload(testResult.imageCapture.casseteNumber),
          isExcluded: false,
          show: true,
          roundNumber: testResult.imageCapture.roundNumber,
          instrumentSerial: serial
        };
        this.cassettes.push(cassette);
      } else {
        // If cassette exists, add the testResult to it
        cassette.testResults.push(testResult);
        return;
      }

      const cassetteInfo = this.cassetteInfo.find(
        c => c.casseteNumber === testResult.imageCapture.casseteNumber &&
          c.instrumentSerial === serial
      );

      if (cassetteInfo) {
        cassette.isExcluded = !!cassetteInfo.isExcluded;
        cassette.cassetteInfo.legendDataId = cassetteInfo.legendDataId; // Update legendDataId in cassetteInfo
        if (cassette.legendDataId !== cassette.cassetteInfo.legendDataId) {
          cassette.isMoved = true;
        }
      }
    });

    this.cassettes.forEach(cassette => {
      // Find the corresponding legendDataItem
      const legendDataItem = this.legendData?.find(
        item => item.legendDataId === cassette.cassetteInfo.legendDataId,
      );
      if (legendDataItem) {
        // Add the cassette to the legendDataItem's cassettes array
        legendDataItem.cassettes.push(cassette);
      }
    });

    // Find cassettes with live notes
    this.subscription.add(
      this.apiService
        .getNotesList('ExperimentId', this.experimentId, 'CassetteLiveNote')
        .subscribe(liveNotes => {
          if (liveNotes) {
            liveNotes.forEach((note: any) => {
              const casseteNumber = this.decodeCassetteId(note.objectId);
              const cassette = this.cassettes.find(c => c.casseteNumber === casseteNumber);
              if (cassette) {
                cassette.hasLiveNote = true;
              }
            });
          }
        }),
    );

    // Sort cassettes within each legendDataItem by casseteNumber
    this.legendData.forEach(legendDataItem => {
      if (legendDataItem.cassettes && legendDataItem.cassettes.length > 0) {
        legendDataItem.cassettes.sort((a, b) => {
          return a.casseteNumber.localeCompare(b.casseteNumber);
        });
      }
    });

    // Set data source
    this.dataSource = new MatTableDataSource<ICLegendData>(this.legendData);

    // Initialize the test results with show property
    this.showAllTestResults();

    this.filterData();
  }

  availableInstruments: { instrumentId: number; serial: string; label: string }[] = [];
  selectedInstrumentSerial: string | null = null;

  onInstrumentChange(): void {
    this.filterData();
  }

  filterData(): void {
    const selectedRound = this.selectedRound;
    const selectedInstrumentSerial = this.selectedInstrumentSerial;



    this.dataSource.data.forEach(legendDataItem => {
      if (!legendDataItem.cassettes) {
        return;
      }

      legendDataItem.cassettes.forEach(cassette => {
        const roundMatch = cassette.roundNumber === selectedRound;

        // Always require an instrument to be selected (no "All Instruments" option)
        let instrumentMatch = false;
        if (selectedInstrumentSerial) {
          // Strict match on the cassette's group serial
          instrumentMatch = cassette.instrumentSerial === selectedInstrumentSerial;
        }

        cassette.show = roundMatch && instrumentMatch;
      });
    });

    // Trigger change detection
    this.dataSource.data = [...this.dataSource.data];
  }

  ngOnInit(): void {
    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });
  }

  ngAfterViewInit() {
    // Usa setTimeout para esperar a que todo el DOM esté listo
    setTimeout(() => {
      this.connectedDropLists = this.dropLists.toArray().map(ref => ref);
    });
  }

  protected ngOnDestroyInternal(): void { }

  private getResultInstrumentSerial(result: ElasticResponseItem): string | null {
  const serialFromImageCapture = result.imageCapture?.instrumentSerial?.trim();
  if (serialFromImageCapture) {
    return serialFromImageCapture;
  }

  const testResultId = result.testResultId?.trim();
  if (!testResultId || !testResultId.includes('-')) {
    return null;
  }

  const candidate = testResultId.split('-')[0]?.trim();
  if (!candidate || candidate.includes('_')) {
    return null;
  }

  return candidate;
  }

  save(): void {
    if (this.newCassetteInfo.length > 0) {
      this.dialogService
        .openConfirmDialog('All previous changes will be saved. Are you sure to continue?', {
          width: '400px',
          isOk: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            // Extract testResultIds for affected cassettes
            const affectedCassetteNumbers = this.newCassetteInfo.map(c => c.casseteNumber).filter((cn): cn is string => !!cn);
            const testResultIds = this.testResults
              .filter(tr => {
                const cassetteNumber = tr.imageCapture?.casseteNumber;
                if (!cassetteNumber) return false;
                // Compare only the part before the dot (like in the web client matching logic)
                const cassetteNumberBase = cassetteNumber.split('.')[0];
                return affectedCassetteNumbers.some(cn => cn && cassetteNumberBase === cn.split('.')[0]);
              })
              .map(tr => tr.testResultId)
              .filter((id): id is string => !!id)
              .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

            this.subscription.add(
              this.apiService.icAdjustLegends(this.icExperimentId, this.newCassetteInfo, testResultIds).subscribe({
                next: () => {
                  this.notificationService.success('Cassette info updated successfully');
                  this.dialogRef.close(this.testResults);
                },
                error: error => {
                  console.error('Error updating cassette info:', error);
                },
              }),
            );
          } else {
            this.notificationService.warn('Changes not saved');
          }
        });
    } else {
      this.dialogService
        .openConfirmDialog('No changes to save. Are you sure to continue?', {
          width: '400px',
          isOk: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this.dialogRef.close(this.testResults);
          }
        });
    }
  }

  cancel(): void {
    if (this.newCassetteInfo.length > 0) {
      this.dialogService
        .openConfirmDialog('All previous changes will be discarded. Are you sure to continue?', {
          width: '400px',
          isOk: false,
        })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            this.dialogRef.close(null);
          }
        });
    } else {
      this.notificationService.warn('No changes to cancel');
      this.dialogRef.close(null);
    }
  }

  showImages(cassetteGroup: ICCassetteGroup): void {
    this.dialog
      .open(ICImagesDialogComponent, {
        width: '90vw',
        maxWidth: '90vw',
        height: '90vh',
        maxHeight: '90vh',
        panelClass: 'large-viewport-dialog',
        data: {
          cassetteGroup: cassetteGroup,
        },
      })
      .afterClosed()
      .subscribe(() => { });
  }

  private getCassetteBaseNumber(cassetteNumber?: string | null): string | null {
    const normalizedCassetteNumber = cassetteNumber?.trim();
    if (!normalizedCassetteNumber) {
      return null;
    }

    const [baseCassetteNumber] = normalizedCassetteNumber.split('.');
    return baseCassetteNumber || null;
  }

  getCassetteImageSetCount(cassette: ICCassetteGroup): number {
    return cassette.testResults.length;
  }

  private getBaseCassetteExpectedImageSetCount(cassette: ICCassetteGroup): number | null {
    const baseCassetteNumber = this.getCassetteBaseNumber(cassette.casseteNumber);
    if (!baseCassetteNumber) {
      return null;
    }

    const iteration = this.legendDataIterations.find(item =>
      item.casseteNumber === baseCassetteNumber &&
      item.roundNumber === cassette.roundNumber,
    );

    if (!iteration || iteration.numberOfImageSets <= 0) {
      return null;
    }

    return iteration.numberOfImageSets;
  }

  getCassetteExpectedImageSetCount(cassette: ICCassetteGroup): number | null {
    if (!this.isPrimaryCassetteUpload(cassette.casseteNumber)) {
      return null;
    }

    return this.getBaseCassetteExpectedImageSetCount(cassette);
  }

  getCassetteImageSetDisplay(cassette: ICCassetteGroup): string {
    const actualImageSetCount = this.getCassetteImageSetCount(cassette);
    const expectedImageSetCount = this.getCassetteExpectedImageSetCount(cassette);

    return expectedImageSetCount != null
      ? `${actualImageSetCount}/${expectedImageSetCount}`
      : `${actualImageSetCount}`;
  }

  isCassetteImageSetComplete(cassette: ICCassetteGroup): boolean {
    const expectedImageSetCount = this.getBaseCassetteExpectedImageSetCount(cassette);
    if (expectedImageSetCount == null) {
      return false;
    }

    const actualImageSetCount = this.getCassetteImageSetCount(cassette);

    return this.isPrimaryCassetteUpload(cassette.casseteNumber)
      ? actualImageSetCount === expectedImageSetCount
      : actualImageSetCount >= expectedImageSetCount;
  }

  getCassetteImageSetTooltip(cassette: ICCassetteGroup): string {
    const actualImageSetCount = this.getCassetteImageSetCount(cassette);
    const expectedImageSetCount = this.getCassetteExpectedImageSetCount(cassette);

    if (expectedImageSetCount != null) {
      return `${actualImageSetCount} of ${expectedImageSetCount} expected image sets have arrived`;
    }

    return actualImageSetCount === 1
      ? 'This cassette contains 1 image set'
      : `This cassette contains ${actualImageSetCount} image sets`;
  }

  add(cassette: ICCassetteGroup): void {
    this.dialog
      .open(ICExcludeIncludeDialogComponent, {
        width: '800px',
        disableClose: true,
        data: { changeType: ICLegendChangeType.Include },
      })
      .afterClosed()
      .subscribe(reason => {
        if (reason !== null) {
          cassette.isExcluded = false;
          const legendDataId = cassette.legendDataId;
          // Find instrumentSerial from testResults
          // let instrumentSerial: string | undefined;
          // if (cassette.testResults && cassette.testResults.length > 0) {
          //   // Try to get from imageCapture first, then fall back to parsing testResultId if needed
          //   const firstResult = cassette.testResults[0];
          //   instrumentSerial = firstResult.imageCapture?.instrumentSerial;
          //   if (!instrumentSerial && firstResult.testResultId) {
          //     const parts = firstResult.testResultId.split('-');
          //     if (parts.length > 0) instrumentSerial = parts[0].trim();
          //   }
          // }

          this.newCassetteInfo.push({
            icExperimentId: this.icExperimentId,
            legendDataId: legendDataId,
            casseteNumber: cassette.casseteNumber,
            isExcluded: false,
            changeType: ICLegendChangeType.Include,
            reason: reason,
            instrumentSerial: cassette.instrumentSerial
          });
        }
      });
  }

  remove(cassette: ICCassetteGroup): void {
    this.dialog
      .open(ICExcludeIncludeDialogComponent, {
        width: '800px',
        disableClose: true,
        data: { changeType: ICLegendChangeType.Exclude },
      })
      .afterClosed()
      .subscribe(reason => {
        if (reason !== null) {
          cassette.isExcluded = true;
          const legendDataId = cassette.legendDataId;

          this.newCassetteInfo.push({
            icExperimentId: this.icExperimentId,
            legendDataId: legendDataId,
            casseteNumber: cassette.casseteNumber,
            isExcluded: true,
            changeType: ICLegendChangeType.Exclude,
            reason: reason,
            instrumentSerial: cassette.instrumentSerial
          });
        }
      });
  }

  // Returns an array of IDs for connected drop lists
  getConnectedDropListsIds(): string[] {
    return this.dataSource.data.map((_, index) => `cdk-drop-list-${index}`);
  }

  onRoundChange(event: any): void {
    console.log('Selected round:', event.value);
    const roundInstrumentOrders = this.getInstrumentOrdersForSelectedRound();

    if (roundInstrumentOrders.length > 0) {
      const activeSerial = this.getActiveInstrumentForRound(
        roundInstrumentOrders[0],
        this.selectedRound,
      ).instrumentSerial;
      if (activeSerial && this.availableInstruments.some(i => i.serial === activeSerial)) {
        this.selectedInstrumentSerial = activeSerial;
      }
    }
    this.filterData();
  }

  private getInstrumentOrdersForSelectedRound(): ICInstrumentOrder[] {
    return [...(this.experiment.instrumentOrders || [])]
      .filter(order => Number(order.roundNumber ?? 1) === Number(this.selectedRound))
      .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
  }

  private getActiveInstrumentForRound(order: ICInstrumentOrder, round: number): { instrumentId: number; instrumentSerial: string } {
    const targetRound = Number(order.roundNumber ?? round);
    const applicableReplacements = (order.replacements || [])
      .filter((r: ICInstrumentOrderReplacement) => Number(r.roundNumber ?? targetRound) === targetRound)
      .sort((a: ICInstrumentOrderReplacement, b: ICInstrumentOrderReplacement) => {
        return this.getReplacementSequenceId(b) - this.getReplacementSequenceId(a);
      });

    if (applicableReplacements.length > 0) {
      const replacement = applicableReplacements[0];
      const serial = (replacement.instrumentSerial || '').trim();
      return {
        instrumentId: replacement.instrumentId || order.instrumentId || 0,
        instrumentSerial: serial,
      };
    }

    const fallbackSerial = (order.originalInstrumentSerial || order.instrumentSerial || '').trim();
    return {
      instrumentId: order.originalInstrumentId || order.instrumentId || 0,
      instrumentSerial: fallbackSerial,
    };
  }

  private getReplacementSequenceId(replacement: ICInstrumentOrderReplacement): number {
    return replacement.instrumentOrderReplacementId || replacement.instrumentReplacementId || 0;
  }

  showAllTestResults(): void {
    this.dataSource.data.forEach(legendDataItem => {
      if (!legendDataItem.cassettes) {
        return;
      }

      legendDataItem.cassettes.forEach(cassette => {
        cassette.show = true; // Show all cassettes
      });
    });

    // Trigger change detection
    this.dataSource.data = [...this.dataSource.data];
  }

  // Returns the ID for a specific drop list based on its index
  dropListId(index: number): string {
    return `cdk-drop-list-${index}`;
  }

  onDrop(event: CdkDragDrop<ICCassetteGroup[]>, targetLegendIndex: number): void {
    // Find the source legend index by locating the dropped cassette in one of the legends
    const sourceLegendIndex = this.dataSource.data.findIndex(row =>
      row.cassettes?.some(c => c === event.item.data),
    );

    if (sourceLegendIndex === -1) {
      console.error('Source legend not found');
      return;
    }

    const sourceLegend = this.dataSource.data[sourceLegendIndex];
    const targetLegend = this.dataSource.data[targetLegendIndex];
    const cassetteToMove = event.item.data as ICCassetteGroup;

    // Find the index of the cassette within the source legend
    const sourceCassetteIndex = sourceLegend.cassettes.findIndex(c => c === cassetteToMove);
    if (sourceCassetteIndex === -1) {
      console.error('Cassette group not found in source legend');
      return;
    }

    const isSameLegend = sourceLegend === targetLegend;

    // Open confirmation dialog before moving the cassette
    this.dialog
      .open(ICExcludeIncludeDialogComponent, {
        width: '800px',
        disableClose: true,
        data: {
          changeType: ICLegendChangeType.Move,
          cassette: cassetteToMove.casseteNumber,
          currentLegend: `${sourceLegend.legendDataId} - ${sourceLegend.name}`,
          newLegend: `${targetLegend.legendDataId} - ${targetLegend.name}`,
        },
      })
      .afterClosed()
      .subscribe(reason => {
        if (reason !== null) {
          if (!isSameLegend) {
            // Remove the cassette from the source legend
            sourceLegend.cassettes.splice(sourceCassetteIndex, 1);

            // Update the cassette group to reflect the new legend
            cassetteToMove.legendDataId = targetLegend.legendDataId;

            // Update legendDataId for each test result in the cassette
            cassetteToMove.testResults.forEach(tr => {
              if (tr.cassetteInfo) {
                tr.cassetteInfo.legendDataId = targetLegend.legendDataId;
              }
            });

            // Insert the cassette into the target legend at the drop position
            targetLegend.cassettes.splice(event.currentIndex, 0, cassetteToMove);
          }

          // Track the move operation to be saved later
          this.newCassetteInfo.push({
            icExperimentId: this.icExperimentId,
            legendDataId: targetLegend.legendDataId,
            casseteNumber: cassetteToMove.casseteNumber,
            isExcluded: false,
            changeType: ICLegendChangeType.Move,
            reason: reason,
            instrumentSerial: cassetteToMove.instrumentSerial
          });

          // Force table refresh to reflect the change in the UI
          this.dataSource.data = [...this.dataSource.data];

          // Order cassettes in both source and target legends by casseteNumber
          sourceLegend.cassettes.sort((a, b) => a.casseteNumber.localeCompare(b.casseteNumber));
          targetLegend.cassettes.sort((a, b) => a.casseteNumber.localeCompare(b.casseteNumber));
        }
      });
  }

  removeFirstVersion(cassetteNumber: string): string {
    // Split the cassette number by '.' and return the first part
    const parts = cassetteNumber.split('.');
    // If the cassette number has a '.' and the second part is '1', return the first part
    if (parts.length > 1 && parts[1] == '1') {
      return parts[0]; // Return the first part of the cassette number
    }
    return cassetteNumber; // If no '.' found, return the original cassette number
  }

  encodeCassetteId(cassetteNumber: string, factor = 1000): number {
    if (!cassetteNumber) {
      throw new Error('cassetteNumber is required');
    }
    const [left, right] = cassetteNumber.split('.');
    const base = parseInt(left, 10);
    if (Number.isNaN(base)) {
      throw new Error(`Invalid base: ${left}`);
    }
    const sub = right ? parseInt(right, 10) : 0;
    if (Number.isNaN(sub)) {
      throw new Error(`Invalid sub: ${right}`);
    }
    const id = base * factor + sub;
    // Check if the id exceeds the int32 range
    if (id > 2147483647) {
      throw new Error(`Encoded id ${id} exceeds int32 range`);
    }
    return id;
  }

  decodeCassetteId(id: number, factor = 1000): string {
    const base = Math.floor(id / factor);
    const sub = id % factor;
    return sub > 0
      ? `${base.toString().padStart(3, '0')}.${sub}`
      : `${base.toString().padStart(3, '0')}`;
  }
}
